import { makeStyles } from '@map/theme';
import { FormControl, Typography } from '@mui/material';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import useErrorMessage from '@shared/hooks/useErrorMessage';
import { uniqueId } from '@shared/util/react';
import { useState } from 'react';
import { CommonFieldProps } from './commonTypes';

const useStyles = makeStyles(({ typography, palette }) => ({
  root: {
    // required, otherwise space to side of the label will toggles the check
    alignItems: 'flex-start',
  },
  label: {
    color: palette.neutrals[800],
    marginRight: 0,
  },
  labelText: typography.body2,
}));

type Props = CommonFieldProps &
  Pick<CheckboxProps, 'onChange' | 'indeterminate'> & {
    className?: string;
    value: boolean;
    title?: string;
    margin?: 'none' | 'dense' | 'normal';
    error?: boolean;
  };

const CheckboxField: FC<Props> = (props) => {
  const {
    className,
    label,
    errorMessageId,
    value,
    helperText,
    title,
    margin = 'normal',
    error,
    ...restProps
  } = props;
  const { classes, cx } = useStyles();
  const [controlId] = useState(() => uniqueId('textfield-'));
  const hasError = error || !!errorMessageId;
  const errorMessage = useErrorMessage(errorMessageId);

  return (
    <FormControl className={cx(classes.root, className)} margin={margin}>
      {title && (
        <Typography
          variant="subtitle1"
          component="label"
          htmlFor={controlId}
          gutterBottom
          className={classes.label}
        >
          {title}
        </Typography>
      )}

      <FormControlLabel
        className={classes.label}
        classes={{ label: classes.labelText }}
        // it's safe to pass ReactNode here, MUI's types are unnecessarily restrictive
        label={label as unknown as string}
        control={<Checkbox color="primary" id={controlId} checked={value} {...restProps} />}
      />
      {(hasError || !!helperText) && (
        <FormHelperText error={hasError}>{errorMessage ?? helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default CheckboxField;
